import HerstellerSlider from "../../components/hersteller-slider/HerstellerSlider";
import "./Hoergeraete.css";
import "../../Leistungen.css";
import HeroSectionLeistungen from "../../components/HeroSectionLeistungen";
import { Helmet } from "react-helmet-async";
import { bauformen } from "../../interfaces/bauformen";
import { technikStufen } from "../../interfaces/technikstufen";
import Accordion from "../../components/Accordion/Accordion";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function Hoergeraete() {
	// Refs für die zu animierenden Elemente
	const bauformenTitleRef = useRef<HTMLHeadingElement>(null);
	const bauformenContainerRef = useRef<HTMLDivElement>(null);
	const technikTitleRef = useRef<HTMLHeadingElement>(null);
	const accordionRef = useRef<HTMLDivElement>(null);
	const funktionenBoxRef = useRef<HTMLDivElement>(null);
	const partnerTitleRef = useRef<HTMLHeadingElement>(null);

	useEffect(() => {
		// ScrollTrigger registrieren
		gsap.registerPlugin(ScrollTrigger);

		// Animation für "Unsere Bauformen im Überblick" - von oben
		if (bauformenTitleRef.current) {
			gsap.fromTo(bauformenTitleRef.current,
				{ opacity: 0, y: -50 },
				{
					opacity: 1,
					y: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: bauformenTitleRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				}
			);
		}

		// Animation für before- und after-Elemente (Dreiecke) über CSS-Klassen
		if (bauformenTitleRef.current) {
			// Animation für before-element (Dreieck links) - von links
			gsap.fromTo(bauformenTitleRef.current,
				{
					opacity: 0,
					y: -50,
					"--before-x": "-100px",
					"--after-x": "100px"
				} as any,
				{
					opacity: 1,
					y: 0,
					"--before-x": "0px",
					"--after-x": "0px",
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: bauformenTitleRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				} as any
			);
		}

		// Animationen für Bauformen-Boxen
		if (bauformenContainerRef.current) {
			const bauformenItems = bauformenContainerRef.current.querySelectorAll('.bauformen-item') as NodeListOf<HTMLElement>;

			bauformenItems.forEach((item: HTMLElement, index: number) => {
				const initialPosition = { opacity: 0, y: 60 } as const;

				gsap.fromTo(item,
					initialPosition,
					{
						opacity: 1,
						x: 0,
						y: 0,
						duration: 1.2,
						ease: "power2.out",
						scrollTrigger: {
							trigger: item,
							start: "top 85%",
							end: "bottom 15%",
							toggleActions: "play none none reverse"
						}
					}
				);
			});
		}

		// Animation für "Technikstufen" - von unten
		if (technikTitleRef.current) {
			gsap.fromTo(technikTitleRef.current,
				{ opacity: 0, y: 50 },
				{
					opacity: 1,
					y: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: technikTitleRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				}
			);
		}

		// Animation für Akkordeon - von unten
		if (accordionRef.current) {
			gsap.fromTo(accordionRef.current,
				{ opacity: 0, y: 60 },
				{
					opacity: 1,
					y: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: accordionRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				}
			);
		}

		// Animation für "Moderne Funktionen & Zubehör" - von unten
		if (funktionenBoxRef.current) {
			gsap.fromTo(funktionenBoxRef.current,
				{ opacity: 0, y: 60 },
				{
					opacity: 1,
					y: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: funktionenBoxRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				}
			);
		}

		// Animation für "Unsere Partner" - von unten
		if (partnerTitleRef.current) {
			gsap.fromTo(partnerTitleRef.current,
				{ opacity: 0, y: 50 },
				{
					opacity: 1,
					y: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: partnerTitleRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none reverse"
					}
				}
			);
		}



		// Cleanup function
		return () => {
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, []);

	return (
		<main>
			<Helmet>
				<link rel="canonical" href="https://hoergut-buehlot.de/Hoergeraete" />
			</Helmet>
			<HeroSectionLeistungen
				title={"H&ouml;rger&auml;te"}
				subtitle={"Ihre Individuelle L&ouml;sung f&uuml;r mehr Lebensqualit&auml;t"}
				image={{ src: "../../img/hoergeraete-logo_gross.webp", alt: "Hörgeräte-Logo", title: "Hörgeräte-Logo", ariaLabel: "Hörgeräte-Logo" }}
				sectionClassName="hoergeraete-hero-section"
				containerClassName="hero-container-leistungen"
			/>

			<div className="box-top-abstand">
				<h3 className="h3-bold" ref={bauformenTitleRef}>Unsere Bauformen im Überblick</h3>
			</div>

			<div className="container">
				<div className="bauformen-container" ref={bauformenContainerRef}>
					{bauformen.map((bauform, index) => (
						<div
							key={index}
							className="bauformen-item"
							style={{
								backgroundColor: bauform.bg_color,
								color: bauform.content_color
							}}
						>
							<h3 style={{ color: bauform.title_color }}>
								{bauform.title}
							</h3>
							<ul>
								{bauform.content.map((item, itemIndex) => (
									<li key={itemIndex}>{item}</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>

			<div className="box-top-abstand box-top-abstand-2">
				<h3 className="h3-bold" ref={technikTitleRef}>Technikstufen – von der <span>Basis</span> bis zur <span>Oberklasse</span></h3>
			</div>

			<div className="container" ref={accordionRef}>
				<Accordion items={technikStufen} />
			</div>

			<div className="container">
				<div className="funktionen-box" ref={funktionenBoxRef}>
					<div className="funktionen-header">
						<h3>Moderne Funktionen & Zubehör</h3>
					</div>
					<div className="funktionen-content">
						<ul className="funktionen-list">
							<li>
								<span className="checkbox-icon"></span>
								Bluetooth: Verbindung mit Handy, TV, Tablet
							</li>
							<li>
								<span className="checkbox-icon"></span>
								Fernbedienung oder App-Steuerung
							</li>
							<li>
								<span className="checkbox-icon"></span>
								TV-Streamer, Telefonadapter, Mikrofone
							</li>
							<li>
								<span className="checkbox-icon"></span>
								Tinnitus-Funktionen (separate Seite)
							</li>
							<li>
								<span className="checkbox-icon"></span>
								Gehörschutz (separate Seite)
							</li>
						</ul>
					</div>
				</div>
			</div>
			<div className="box-top-abstand box-top-abstand-3">
				<h3 className="h3-bold" ref={partnerTitleRef}>Unsere Partner</h3>
			</div>
			<HerstellerSlider />
		</main>
	)
}
